// Monthly loan terms for Business Loan and GP Loan: loan amount, EMI, first due date and
// EMI day, worked out exactly as the reports do. Pure functions, no imports, so the backend
// keeps an identical copy (backend/utils/monthlyLoanTerms.js) for the foreclosure quote -
// change both. Moved here unchanged from utils.js, which re-exports them.

export const getDisbursementAmount = (data) => {
  if (!data) return 0;
  if (data.loanType === "GP Loan") {
    return (
      parseFloat(data.eligibleLoanAmountByBm) ||
      parseFloat(data.totalLoanAmountByBm) ||
      parseFloat(data.goldloantotal) ||
      parseFloat(data.sanctionedLoanAmountBySanctionCommittee) ||
      parseFloat(data.proposedLoanDetails?.loanAmount) ||
      0
    );
  }

  // Prioritize Gross Sanctioned Amount for EMI calculations
  const grossAmount = parseFloat(data.sanctionedLoanAmountBySanctionCommittee);
  if (grossAmount > 0) return grossAmount;

  // Fallback to transactionAmount (net) or proposed amount
  return data.transactionAmount && parseFloat(data.transactionAmount) > 0
    ? parseFloat(data.transactionAmount)
    : parseFloat(data.proposedLoanDetails?.loanAmount) || 0;
};

/**
 * Single source of truth for "does this receipt count as money received?".
 *
 * Every calculation - Master, Outstanding, Overdue/DPD, PAR, Account Statement
 * and the Collection reports - must ask this one function rather than testing
 * receipt.status inline. Scattered copies of that test are what previously let
 * voided money keep appearing in some reports but not others.
 *
 * A receipt does NOT count when:
 *   - it has been rejected / cancelled / retriggered / deleted, or
 *   - its collection approval was retriggered or rejected by any role.
 */
export const VOIDED_RECEIPT_STATUSES = ["rejected", "cancelled", "retriggered", "deleted"];

export const isValidPaidReceipt = (receipt) => {
  if (!receipt) return false;
  const status = String(receipt.status || "").trim().toLowerCase();
  if (VOIDED_RECEIPT_STATUSES.includes(status)) return false;
  return status === "paid";
};

export const getEmiDay = (data) => {
  if (!data) return null;
  return (
    data.emiDateByBranchManager ||
    data.proposedLoanDetails?.emiDate ||
    data.dayOrderByBranchManager ||
    null
  );
};

const getLastDayOfMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * The shortest gap the business allows between disbursement and the first
 * instalment. A CRO does not collect from a customer a few days after handing
 * over the money: for a loan disbursed on 28-Aug with an EMI day of the 8th,
 * collection starts on 08-Oct, not 08-Sep.
 *
 * Used in two places, which is why it lives here: the Branch Manager's
 * suggested start date, and the check on whether a late first receipt is an
 * expected skip or a genuinely missed payment (bugfix.md Bug 24).
 */
export const MIN_DAYS_TO_FIRST_EMI = 15;

/**
 * @param {Date|string} disbursementDate
 * @param {number} tenureInMonths
 * @param {string|number} emiDay - the recurring day of the month
 * @param {string|null} installmentStartDate - the date the Branch Manager
 *   recorded for the first instalment. When present it wins outright: it is
 *   what was agreed with the customer, not a guess. NULL on every loan
 *   disbursed before that field existed, which falls back to the derivation
 *   below, unchanged.
 */
export const getFirstAndLastEmiDates = (
  disbursementDate,
  tenureInMonths,
  emiDay,
  installmentStartDate = null
) => {
  try {
    // Validate displacementDate and tenureInMonths
    if (!disbursementDate || !tenureInMonths || tenureInMonths <= 0 || !emiDay) {
      return { firstEmiDate: null, lastEmiDate: null };
    }

    // An explicitly recorded start date wins over anything derived.
    if (installmentStartDate) {
      const startParts = String(installmentStartDate).slice(0, 10).split("-");
      if (startParts.length === 3) {
        const startDate = new Date(
          parseInt(startParts[0]),
          parseInt(startParts[1]) - 1,
          parseInt(startParts[2])
        );
        if (!isNaN(startDate.getTime())) {
          const startDay = startDate.getDate();
          const lastEmiDate = new Date(startDate);
          lastEmiDate.setMonth(
            lastEmiDate.getMonth() + (parseInt(tenureInMonths) - 1)
          );
          // A short month rolls the date forward - 31 Jan + 1 month is 3 Mar.
          // Pull it back to the last day of the intended month instead.
          if (lastEmiDate.getDate() !== startDay) {
            lastEmiDate.setDate(0);
          }
          return { firstEmiDate: startDate, lastEmiDate };
        }
      }
    }

    const disbursement = new Date(disbursementDate);

    // Determine if emiDay is a full date string (YYYY-MM-DD or similar) or just a day number
    let day = parseInt(emiDay);

    // If emiDay is a date string (like "2024-05-15"), use it directly
    let emiDayNormalized = emiDay;

    // If emiDay is a full date string like "2024-04-15", use it directly as the first EMI date
    if (emiDayNormalized && typeof emiDayNormalized === 'string' && emiDayNormalized.includes('-')) {
      const parts = emiDayNormalized.split("-");
      if (parts.length === 3) {
        // Create date using year, month (0-indexed), and day to ensure it's in local time
        const selectedDate = new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2])
        );

        if (!isNaN(selectedDate.getTime())) {
          // User selected a specific start date for the EMI
          let firstEmiDate = new Date(selectedDate);
          let lastEmiDate = new Date(selectedDate);
          const targetLastMonth =
            selectedDate.getMonth() + (parseInt(tenureInMonths) - 1);
          lastEmiDate.setMonth(targetLastMonth);

          // Ensure the last day of month is handled correctly if emiDay was high (e.g., 31)
          const targetDay = selectedDate.getDate();
          if (lastEmiDate.getDate() !== targetDay) {
            lastEmiDate.setDate(0); // Set to last day of previous month
          }

          // Safety check: Ensure first EMI date is strictly AFTER disbursement date
          // If they are on the same day, move to next month
          if (firstEmiDate <= disbursement) {
            const nextMonth = firstEmiDate.getMonth() + 1;
            const nextYear = firstEmiDate.getFullYear() + (nextMonth > 11 ? 1 : 0);
            const adjustedMonth = nextMonth % 12;
            const tDay = firstEmiDate.getDate();
            firstEmiDate = new Date(nextYear, adjustedMonth, Math.min(tDay, getLastDayOfMonth(nextYear, adjustedMonth)));

            // Update lastEmiDate based on adjusted firstEmiDate
            lastEmiDate = new Date(
              firstEmiDate.getFullYear(),
              firstEmiDate.getMonth() + (parseInt(tenureInMonths) - 1),
              Math.min(tDay, getLastDayOfMonth(firstEmiDate.getFullYear(), firstEmiDate.getMonth() + (parseInt(tenureInMonths) - 1)))
            );
          }

          return { firstEmiDate, lastEmiDate };
        }
      }
    }

    // Logic for day integer (1-31)
    if (isNaN(day) || day < 1 || day > 31) {
      return { firstEmiDate: null, lastEmiDate: null };
    }

    // Validate disbursement date
    if (isNaN(disbursement.getTime())) {
      return { firstEmiDate: null, lastEmiDate: null };
    }

    // Normalize disbursement to local date parts to avoid timezone shifting issues
    const disburYear = disbursement.getFullYear();
    const disburMonth = disbursement.getMonth();
    const disburDate = disbursement.getDate();

    // Business Rule: First EMI always starts in the month AFTER disbursement
    let startMonth = disburMonth + 1;

    let firstEmiDate = new Date(
      disburYear,
      startMonth,
      Math.min(day, getLastDayOfMonth(disburYear, startMonth))
    );

    // Safety check: Ensure first EMI date is strictly AFTER disbursement date
    // If they are on the same day, move to next month
    if (firstEmiDate <= disbursement) {
      const nextMonth = firstEmiDate.getMonth() + 1;
      const nextYear = firstEmiDate.getFullYear() + (nextMonth > 11 ? 1 : 0);
      const adjustedMonth = nextMonth % 12;
      firstEmiDate = new Date(
        nextYear,
        adjustedMonth,
        Math.min(day, getLastDayOfMonth(nextYear, adjustedMonth))
      );
    }

    const lastEmiDate = new Date(
      firstEmiDate.getFullYear(),
      firstEmiDate.getMonth() + (parseInt(tenureInMonths) - 1),
      Math.min(day, getLastDayOfMonth(firstEmiDate.getFullYear(), firstEmiDate.getMonth() + (parseInt(tenureInMonths) - 1)))
    );

    return { firstEmiDate, lastEmiDate };
  } catch (error) {
    console.error('Error calculating EMI dates:', error);
    return { firstEmiDate: null, lastEmiDate: null };
  }
};

/**
 * Works out which date a loan's instalments really start from.
 *
 * Three sources, in order of authority:
 *
 *  1. `installmentStartDate` - what the Branch Manager recorded at
 *     disbursement. This is an agreement with the customer, so it wins.
 *
 *  2. The customer's first receipt, for loans disbursed before that field
 *     existed. What the CRO actually collected beats what a formula predicts -
 *     but only where it is safe, see the guard below.
 *
 *  3. Nothing, in which case the caller derives the date as it always has.
 *
 * The guard on rule 2 matters. A first receipt EARLIER than the formula date is
 * unambiguous - the CRO simply started sooner - so it is always trusted. A
 * first receipt LATER than the formula date has two possible meanings that the
 * receipt alone cannot separate:
 *
 *   - the CRO skipped a month because the formula date fell too soon after
 *     disbursement (legitimate, and the reason this feature exists), or
 *   - the customer PAID LATE.
 *
 * Treating both the same would move the schedule to the late payment and the
 * delay would stop counting as overdue. On the current book that would quietly
 * clear 12 genuinely late loans out of PAR. So a later receipt is accepted only
 * when the formula date sat inside MIN_DAYS_TO_FIRST_EMI of disbursement, where
 * a skip was expected; otherwise the loan keeps its derived schedule and stays
 * overdue (bugfix.md Bug 24).
 *
 * JLG is excluded throughout: those loans repay on the centre meeting day, and
 * no meeting is skipped.
 *
 * @param {object} data - a loan row, with receiptsDetails when available
 * @returns {string|Date|null} the start date to use, or null to derive it
 */
export const resolveEffectiveStartDate = (data) => {
  if (!data) return null;
  if (data.installmentStartDate) return data.installmentStartDate;
  if (data.loanType === "JLG Loan") return null;

  const paidReceipts = (data.receiptsDetails || []).filter(isValidPaidReceipt);
  if (paidReceipts.length === 0) return null;

  const earliestReceipt = paidReceipts.reduce((earliest, receipt) =>
    String(receipt.emiDate) < String(earliest.emiDate) ? receipt : earliest
  );
  const receiptDate = new Date(String(earliestReceipt.emiDate).slice(0, 10));
  if (isNaN(receiptDate.getTime())) return null;

  // What the formula would have said, so the two can be compared.
  const { firstEmiDate: derivedDate } = getFirstAndLastEmiDates(
    data.branchManagerStatusUpdatedAt,
    data.proposedLoanDetails?.tenureInMonths,
    getEmiDay(data)
  );
  if (!derivedDate) return null;

  if (receiptDate < derivedDate) return earliestReceipt.emiDate;

  const daysFromDisbursement = Math.round(
    (derivedDate - new Date(data.branchManagerStatusUpdatedAt)) / 86400000
  );
  return daysFromDisbursement < MIN_DAYS_TO_FIRST_EMI
    ? earliestReceipt.emiDate
    : null;
};

// Utility function to get EMI dates within the range of first EMI and last EMI
export const getEmiDatesInRange = (
  emiDay,
  fromDate,
  toDate,
  firstEmiDate,
  lastEmiDate
) => {
  const emiDates = [];
  const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

  let parsedFrom = fromDate ? new Date(fromDate) : null;
  let parsedTo = toDate ? new Date(toDate) : null;

  const from = isValidDate(parsedFrom) ? parsedFrom : (firstEmiDate ? new Date(firstEmiDate) : new Date(0));
  const to = isValidDate(parsedTo) ? parsedTo : (lastEmiDate ? new Date(lastEmiDate) : new Date());

  let currentDate = new Date(from);

  // Extract day from emiDay
  let day = parseInt(emiDay);
  if (isNaN(day) || day < 1 || day > 31) {
    const date = new Date(emiDay);
    if (!isNaN(date.getTime())) {
      day = date.getDate();
    } else {
      return [];
    }
  }

  while (currentDate <= to) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const emiDate = new Date(
      year,
      month,
      Math.min(day, getLastDayOfMonth(year, month))
    );

    // FIX: Include emiDate >= from to respect the requested range
    if (
      emiDate >= from &&
      emiDate >= firstEmiDate &&
      emiDate <= lastEmiDate &&
      emiDate <= to
    ) {
      emiDates.push(emiDate); // Pushing the `emiDate` as it is
    }

    // Move to the 1st of the next month to avoid skipping months
    currentDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1
    );
  }
  return emiDates;
};

// The bare reducing-balance formula, with no validation, guarding or
// rounding of its own - callers each have different needs for all three
// (see calculateEmiAmount and calculateTotalLoanInterest below, which used
// to each hand-write this same line separately). Exported only for utils.js;
// every other caller should go through calculateEmiAmount, which already
// validates and guards inputs.
export const rawMonthlyEmi = (amount, rate, tenure) => {
  const monthlyRate = rate / 12 / 100;
  const denominator = Math.pow(1 + monthlyRate, tenure) - 1;
  return { emi: (amount * monthlyRate * Math.pow(1 + monthlyRate, tenure)) / denominator, denominator };
};

export const calculateEmiAmount = (sanctionedAmount, rateOfInterest, tenureInMonths) => {
  try {
    // Validate inputs
    const amount = parseFloat(sanctionedAmount);
    const rate = parseFloat(rateOfInterest);
    const tenure = parseInt(tenureInMonths);

    if (!amount || amount <= 0 || rate < 0 || !tenure || tenure <= 0) {
      console.warn('Invalid EMI calculation inputs:', { sanctionedAmount, rateOfInterest, tenureInMonths });
      return 0;
    }

    // Handle zero interest rate case
    if (rate === 0) {
      return amount / tenure;
    }

    const { emi: emiAmount, denominator } = rawMonthlyEmi(amount, rate, tenure);

    // Avoid division by zero
    if (denominator === 0) {
      return amount / tenure;
    }

    // Return as number, not string
    return Math.round(emiAmount * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    console.error('Error calculating EMI amount:', error);
    return 0;
  }
};

export const getEffectiveEmi = (data) => {
  // Re-calculate based on the final disbursement amount first if available, 
  // as this represents the final approved loan amount which may differ from the proposal.
  const disbursementAmount = getDisbursementAmount(data);
  if (disbursementAmount > 0) {
    return calculateEmiAmount(
      disbursementAmount,
      data?.proposedLoanDetails?.rateOfInterest || 0,
      data?.proposedLoanDetails?.tenureInMonths || 0
    );
  }

  // Priority 1: Use stored monthlyEmi from proposedLoanDetails if available and valid
  const storedEmi = parseFloat(data?.proposedLoanDetails?.monthlyEmi);
  if (storedEmi > 0) return storedEmi;

  // Priority 2: Use stored monthlyEmi from top level if available
  const topLevelEmi = parseFloat(data?.monthlyEmi);
  if (topLevelEmi > 0) return topLevelEmi;

  // Priority 3: Fallback calculation
  return calculateEmiAmount(
    data?.proposedLoanDetails?.loanAmount || 0,
    data?.proposedLoanDetails?.rateOfInterest || 0,
    data?.proposedLoanDetails?.tenureInMonths || 0
  );
};

const ymdLocal = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * Everything the repayment schedule needs for a Business Loan or GP Loan, worked out
 * exactly as the reports do. The reports (buildMonthlyLoanSummary in utils.js) and the
 * backend foreclosure quote both call this, so a foreclosure quote always agrees with
 * the Outstanding Principal on the reports.
 *
 * @returns {object|null} null when the loan cannot be scheduled (missing amount, rate,
 *   tenure, EMI or EMI dates)
 */
export const getMonthlyLoanTerms = (data) => {
  if (!data || (data.loanType !== "Business Loan" && data.loanType !== "GP Loan")) return null;
  const loanAmount = parseFloat(getDisbursementAmount(data));
  const annualRatePercent = parseFloat(data.proposedLoanDetails?.rateOfInterest);
  const tenure = parseInt(data.proposedLoanDetails?.tenureInMonths);
  if (isNaN(loanAmount) || isNaN(annualRatePercent) || isNaN(tenure)) return null;
  if (data.loanType === "Business Loan" && (loanAmount <= 0 || tenure <= 0)) return null;

  const rawEmiAmount = getEffectiveEmi(data);
  if (isNaN(rawEmiAmount) || rawEmiAmount <= 0) return null;

  const dueDay = getEmiDay(data);
  const { firstEmiDate, lastEmiDate } = getFirstAndLastEmiDates(
    data.branchManagerStatusUpdatedAt,
    data.proposedLoanDetails?.tenureInMonths,
    dueDay,
    resolveEffectiveStartDate(data)
  );
  if (!firstEmiDate || !lastEmiDate) return null;

  const firstDue =
    getEmiDatesInRange(dueDay, firstEmiDate, lastEmiDate, firstEmiDate, lastEmiDate)[0] || firstEmiDate;
  return {
    loanAmount,
    annualRatePercent,
    tenure,
    rawEmiAmount,
    emiAmount: Math.round(rawEmiAmount),
    dueDay,
    firstEmiDate,
    lastEmiDate,
    firstDueDate: ymdLocal(firstDue),
  };
};
